import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import { ArrowRight, Crown, Sparkles, Users } from 'lucide-react';

const TIER_UPGRADES = {
  flex: {
    from: { label: 'Team Flex', seats: 5 },
    to: {
      key: 'pro',
      label: 'Pro Betrieb',
      seats: 15,
      priceHint: '249 €/Monat',
      envKey: 'DIGISTORE_TIER_PRO',
      wins: [
        'Bis zu 15 Mitarbeiter (statt 5)',
        'SmartVolt Copilot 24/7',
        'Wochen-Ampel-Mails automatisch',
        'Social-Media-Badges für Zertifikate',
      ],
    },
  },
  pro: {
    from: { label: 'Pro Betrieb', seats: 15 },
    to: {
      key: 'enterprise',
      label: 'Enterprise',
      seats: '∞',
      priceHint: '499 €/Monat',
      envKey: 'DIGISTORE_TIER_ENTERPRISE',
      wins: [
        'Unlimitierte Mitarbeiter',
        'A-Kunden-Priorität für Wünsche',
        'Persönlicher Ansprechpartner',
        'White-Label-Zertifikate mit Logo',
      ],
    },
  },
};

const TierUpgradePrompt = ({ open, onOpenChange, currentTier, checkoutUrl, seatsUsed, seatsTotal }) => {
  const upgrade = TIER_UPGRADES[currentTier];
  if (!upgrade) return null;

  const handleUpgrade = () => {
    if (checkoutUrl) {
      window.open(checkoutUrl, '_blank', 'noopener,noreferrer');
    }
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-lg bg-gradient-to-b from-gray-900 to-black border border-yellow-500/40 text-white"
        data-testid="tier-upgrade-prompt"
      >
        <DialogHeader>
          <div className="flex items-center gap-2 mb-2">
            <div className="p-2 rounded-full bg-yellow-500/15 border border-yellow-500/30">
              <Crown className="h-5 w-5 text-yellow-400" />
            </div>
            <DialogTitle className="text-2xl text-yellow-400">
              Mitarbeiter-Limit erreicht
            </DialogTitle>
          </div>
          <DialogDescription className="text-gray-300 text-base leading-relaxed">
            Dein <strong className="text-yellow-300">{upgrade.from.label}</strong>-Paket ist mit{' '}
            <strong className="text-white">{seatsUsed}/{seatsTotal}</strong> Plätzen ausgeschöpft.
            Zeit für den nächsten Schritt – dein Team wächst!
          </DialogDescription>
        </DialogHeader>

        <div className="mt-4 p-4 rounded-lg bg-yellow-500/5 border border-yellow-500/20">
          <div className="flex items-center gap-2 mb-3">
            <Sparkles className="h-4 w-4 text-yellow-400" />
            <span className="text-sm font-semibold text-yellow-300 uppercase tracking-wider">
              Empfohlenes Upgrade
            </span>
          </div>
          <div className="flex items-baseline justify-between mb-3">
            <h3 className="text-xl font-bold text-white">{upgrade.to.label}</h3>
            <span className="text-yellow-400 font-semibold">{upgrade.to.priceHint}</span>
          </div>
          <div className="flex items-center gap-2 text-sm text-gray-400 mb-4">
            <Users className="h-4 w-4" />
            <span>Bis zu {upgrade.to.seats} Mitarbeiter inklusive</span>
          </div>
          <ul className="space-y-2 text-sm text-gray-200">
            {upgrade.to.wins.map((win, i) => (
              <li key={i} className="flex items-start gap-2">
                <span className="text-yellow-400 mt-0.5">✓</span>
                <span>{win}</span>
              </li>
            ))}
          </ul>
        </div>

        <div className="flex gap-3 mt-6">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            className="flex-1 border-gray-700 text-gray-300 hover:bg-gray-800"
            data-testid="tier-upgrade-later-btn"
          >
            Später
          </Button>
          <Button
            onClick={handleUpgrade}
            disabled={!checkoutUrl}
            className="flex-1 bg-yellow-500 hover:bg-yellow-400 text-black font-bold"
            data-testid="tier-upgrade-checkout-btn"
          >
            Jetzt upgraden
            <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
        </div>
        {!checkoutUrl && (
          <p className="text-xs text-red-300 text-center mt-2">
            Checkout-Link nicht verfügbar – bitte Support kontaktieren.
          </p>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default TierUpgradePrompt;
